<script setup>
import { computed, ref } from 'vue';

defineOptions({
  inheritAttrs: false
});

const model = defineModel({ default: '' });
const props = defineProps({
  id: {
    type: String,
    default: 'form-input'
  },
  type: {
    type: String,
    default: 'text',
    required: true,
    validator: (type) => ['text', 'email', 'password'].includes(type)
  },
  label: {
    type: String,
  },
  placeholder: {
    type: String
  },
  inputTheme: {
    type: String,
    default: 'dark',
    validator: (theme) => ['light', 'dark'].includes(theme)
  },
  size: {
    type: String,
    default: 'md',
    validator: (size) => ['xs', 'sm', 'md', 'lg'].includes(size)
  },

  validationStatus: {
    type: String,
    validator: (type) => ['error', 'success', undefined].includes(type)
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const inputRef = ref(null);
const validationTransparentRef = ref(null);
const isFocused = ref(false);
const revealPassword = ref(false);

const inputType = computed(() => {
  if (props.type === 'password') {
    return revealPassword.value ? 'text' : 'password';
  }

  return props.type;
});
const showPasswordToggle = computed(() => props.type === 'password' && model.value?.length);

const inputClasses = computed(() => ({
  focused: !!isFocused.value,

  'block px-0 w-full bg-transparent border-0 border-b appearance-none focus:outline-hidden focus:ring-0 peer': true,

  'px-1 py-1.5 text-xs': props.size === 'xs',
  'px-1.5 py-2 text-sm': props.size === 'sm',
  'px-2 py-2.5 text-base': props.size === 'md',
  'px-2.5 py-2.5 text-lg': props.size === 'lg',

  'text-gray-400 placeholder:text-gray-400': props.disabled,
  'text-gray-900 placeholder:text-gray-900': !props.disabled && props.inputTheme === 'dark',
  'text-white placeholder:text-white': !props.disabled && props.inputTheme === 'light',

  'border-gray-400': props.disabled,
  'border-gray-900 focus:border-black': !props.disabled && props.inputTheme === 'dark' && !props.validationStatus,
  'border-white/25 focus:border-white': !props.disabled && props.inputTheme === 'light' && !props.validationStatus,

  'border-red-600': props.validationStatus === 'error',
  'border-green-600': props.validationStatus === 'success',
  'text-transparent! placeholder:text-transparent! has-transparent-error': validationTransparentRef.value && props.validationStatus && !isFocused.value,

  'password-input': props.type === 'password',
  'input-theme-dark': props.inputTheme === 'dark',
  'input-theme-light': props.inputTheme === 'light'
}));

const labelClasses = computed(() => ({
  'absolute duration-300 transform left-0 -z-10 origin-[0] peer-focus:start-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:rtl:translate-x-1/4 peer-focus:rtl:left-auto capitalize truncate text-ellipsis text-start': true,

  'top-1.5 text-xs scale-[.80] peer-focus:scale-[.80] -translate-y-4 peer-focus:-translate-y-4': props.size === 'xs',
  'top-2 text-sm scale-75 peer-focus:scale-75 -translate-y-5 peer-focus:-translate-y-5': props.size === 'sm',
  'top-3 text-base scale-[.65] peer-focus:scale-[.65] -translate-y-6 peer-focus:-translate-y-6': props.size === 'md',
  'top-3 text-lg scale-[.55] peer-focus:scale-[.55] -translate-y-6 peer-focus:-translate-y-6': props.size === 'lg',

  'text-gray-400': props.disabled,
  'text-gray-500 peer-focus:text-black': !props.disabled && props.inputTheme === 'dark' && (!props.validationStatus || !validationTransparentRef.value || isFocused.value),
  'text-white peer-focus:text-white': !props.disabled && props.inputTheme === 'light' && (!props.validationStatus || !validationTransparentRef.value || isFocused.value),

  'text-red-600': !props.disabled && props.validationStatus === 'error' && validationTransparentRef.value && !isFocused.value,
  'text-green-600': !props.disabled && props.validationStatus === 'success',

  'hidden!': validationTransparentRef.value && props.validationStatus && !isFocused.value
}));

const validationWrapperClasses = computed(() => ({
  'mt-1 text-xs': props.size === 'xs',
  'mt-1.5 text-sm': props.size === 'sm',
  'mt-2 text-base': props.size === 'md',
  'mt-2.5 text-lg': props.size === 'lg',

  'text-green-600': props.validationStatus === 'success',
  'text-red-600': props.validationStatus === 'error'
}));

const validationTransparentClasses = computed(() => ({
  'absolute top-0 left-0 right-0 bottom-0.5 flex items-center justify-center text-center': true,

  // This is used to hide the select arrow when validation message is shown
  'validation-transparent': !isFocused.value,
  hidden: isFocused.value,

  'text-[10px]/[10px]': props.size === 'xs',
  'text-xs': props.size === 'sm',
  'text-sm': props.size === 'md',
  'text-base': props.size === 'lg',

  'bg-green-600/25 text-white': props.validationStatus === 'success',
  'bg-red-600/25 text-white animated shake': props.validationStatus === 'error'
}));

const pwToggleClasses = computed(() => ({
  'password-toggle-btn border-none group flex items-center justify-center text-white font-semibold bg-transparent focus:outline-hidden rounded-full': true,
  'absolute right-0 top-0': true,

  'p-1.5 w-8 h-8': props.size === 'xs',
  'p-2 w-8 h-8': props.size === 'sm',
  'p-2.5 w-12 h-12': props.size === 'md',
  'p-3 w-12 h-12': props.size === 'lg',
}));

function onFocus (e) {
  isFocused.value = true;
}

function onBlur (e) {
  isFocused.value = false;
}

function focus () {
  if (inputRef.value) {
    inputRef.value.focus();
  }
}

defineExpose({
  focus,
  blur
});
</script>

<template>
  <div>
    <div class="form-input-wrapper relative z-0 text-xs">
      <input
        ref="inputRef"
        v-bind="$attrs"
        v-model="model"
        :id="id"
        :type="inputType"
        :class="inputClasses"
        :disabled="disabled"
        :required="required"
        :placeholder="label ? ' ' : `${required ? '* ' : ''}${$t(placeholder)}`"
        @focus="onFocus"
        @blur="onBlur"
      />
      <label
        v-if="label"
        :for="id"
        :class="labelClasses"
      >
        {{ required ? '* ' : '' }}{{ $t(label) }}
      </label>

      <p
        v-if="$slots.validationTransparent"
        ref="validationTransparentRef"
        :class="validationTransparentClasses"
        @click="focus"
      >
        <slot name="validationTransparent" />
      </p>

      <button
        v-if="showPasswordToggle"
        type="button"
        :class="pwToggleClasses"
        data-component-element-id="form-input-reveal-password-btn"
        @mousedown.prevent="revealPassword = !revealPassword"
      >
        <img
          v-if="!revealPassword"
          class="w-full h-full"
          src="@/static/icons/eyes_on_black.svg"
          alt="Show Password Icon"
        />
        <img
          v-else
          class="w-full h-full"
          src="@/static/icons/eyes_off_black.svg"
          alt="Hide Password Icon"
        />
      </button>
    </div>

    <p
      v-if="$slots.validationMessage"
      :class="validationWrapperClasses"
    >
      <slot name="validationMessage" />
    </p>
  </div>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

.form-input-wrapper:has(> .validation-transparent):not(.hidden) > .password-toggle-btn {
  display: none !important;
}

:deep(input) {
  &::placeholder {
    @apply capitalize;
  }

  // This is a workaround for the autofill background color issue in Chrome and Firefox
  &.input-theme-light {
    /* Chrome, Safari, Edge */
    &:-webkit-autofill {
      background-color: #1f293720 ;
      color: white ;
      -webkit-background-clip: text;
      -webkit-text-fill-color: white;
      transition: background-color 5000s ease-in-out 0s;
      box-shadow: inset 0 0 20px 20px #1f293729;
    }
    /* Firefox */
    &:-moz-autofill {
      background-color: #1f293720;
      color: white;
      transition: background-color 5000s ease-in-out 0s;
      box-shadow: inset 0 0 20px 20px #1f293729;
    }
  }

  // This is a workaround for the autofill text color issue in Chrome and Firefox
  // and to make the error message visible when the input is not focused
  &.has-transparent-error {
    &.input-theme-light {
      /* Chrome, Safari, Edge */
      &:-webkit-autofill {
        -webkit-text-fill-color: transparent !important;
      }
      /* Firefox */
      &:-moz-autofill {
        color: transparent !important;
      }
    }
  }
}
</style>
